import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Divider,
  Grid,
  Paper,
  TextField,
  Typography
} from '@mui/material'
import { useMutation, useQuery } from '@tanstack/react-query'
import PaymentCollectionApi, {
  PaymentCollectionReceiptBackendData
} from 'api/b2b/PaymentCollectionApi'
import B2bTDPAPI from 'api/b2b/TdpApi'
import OrbitSession from 'hooks/useCookies'
import useDebounce from 'hooks/useDebounce'

import { ConfirmOptions } from '../constants'

import InvoiceDetailsList from './InvoiceDetails'
import PaymentDuesList from './PaymentDuesList'

interface Company {
  tenant_id: string
  company_name: string
  id: string
  currency: string
  commercial_type: string
}

interface TenantData {
  data: Company[]
}

interface CompanyOption {
  label: string
  value: string
  companyId: string
  currency: string
  paymentMode: string
}

export interface IAutoCompleteDropdown {
  label: string
  value: string
}

interface FormData {
  companyName: CompanyOption | null
  companyId: string
  orderDateFrom: string
  orderDateTo: string
  orderNo: string
  promoteEsim: IAutoCompleteDropdown
  paymentMode: string
}

// New interface for payment form fields
interface PaymentFormData {
  paymentMode: string
  totalAmountToReceive: number
  referenceNumber: string
  remarks: string
}

export type PendingPaymentTableProps = {
  company_name: string
  company_id: string
  currency: string
  wallet_balance: number
  outstanding_dues: number
}

export interface InvoiceDetailsProps {
  company_name: string
  order_id: string
  product: string
  currency: string
  order_amount: number
  paid_amount: number
  pending_amount: number
  order_date: string
  package_name: string
  due_date: string
  discount?: number
  invoice_id: string
}

export default function AddPaymentCollection() {
  const token = OrbitSession.get('faberToken')
  const userEmail = OrbitSession.get('email')

  const [companySearchTerm, setCompanySearchTerm] = useState('')
  const debouncedSearchTerm = useDebounce(companySearchTerm, 500)
  const [tableData, setTableData] = useState<PendingPaymentTableProps[]>([])
  const [invoiceTableData, setInvoiceTableData] = useState<
    InvoiceDetailsProps[]
  >([])
  const [selectedRowIds, setSelectedRowIds] = useState<string[]>([])
  const [discountValues, setDiscountValues] = useState<Record<string, number>>(
    {}
  )

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    getValues: getFormValues
  } = useForm<FormData>({
    defaultValues: {
      companyName: null,
      companyId: '',
      orderDateFrom: '',
      orderDateTo: ''
    }
  })

  // Payment form
  const {
    control: paymentControl,
    watch: watchPayment,
    reset: resetPayment,
    getValues,
    setValue: setPaymentValue
  } = useForm<PaymentFormData>({
    defaultValues: {
      paymentMode: 'WALLET', // Default disabled value
      totalAmountToReceive: 0,
      referenceNumber: '',
      remarks: ''
    }
  })

  const navigate = useNavigate()

  // Watch form values
  const companyName = watch('companyName')
  const totalAmountToReceive = watchPayment('totalAmountToReceive')

  // Get wallet balance for validation
  const walletBalance = useMemo(() => {
    return tableData[0]?.wallet_balance || 0
  }, [tableData])

  // Get selected row data
  const selectedRowData = useMemo(() => {
    if (!selectedRowIds?.length || !invoiceTableData.length) return []
    return invoiceTableData
      .filter((row) => selectedRowIds.includes(row.order_id))
      .map((row) => ({
        ...row,
        allocatedAmount: discountValues?.[row.order_id] ?? 0
      }))
  }, [selectedRowIds, invoiceTableData, discountValues])

  // 2) Total allocated from selected rows
  const totalAllocated = useMemo(
    () =>
      selectedRowData.reduce(
        (sum, r) => sum + (Number(r.allocatedAmount) || 0),
        0
      ),
    [selectedRowData]
  )

  const isAmountValid = totalAmountToReceive <= walletBalance

  // 3) Optional: gate the submit button (example)
  const canSubmitPayment =
    selectedRowData.length > 0 &&
    selectedRowData.some((r) => r.allocatedAmount > 0) &&
    isAmountValid

  // Debounced company search query
  const {
    data: tenantData,
    isLoading: isTenantLoading,
    isError: tenantFetchError
  } = useQuery<TenantData>(
    ['tenant-search', debouncedSearchTerm],
    async () => {
      const token = OrbitSession.get('faberToken')
      const { data } = await B2bTDPAPI.getTDPDetails(token, {
        company_name: debouncedSearchTerm
      })
      return data
    },
    {
      enabled: debouncedSearchTerm.length >= 2,
      staleTime: 2 * 60 * 1000,
      cacheTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
      onError: (error) => {
        console.error('Failed to fetch companies:', error)
      }
    }
  )

  const {
    mutate: unpaidPaymentAllocation,
    isLoading: unpaidPaymentAllocationLoading
  } = useMutation({
    mutationFn: async (params: Record<string, string>) => {
      const { data } = await PaymentCollectionApi.getUnpaidPaymentAllocation(
        token,
        params
      )
      return data
    },
    onSuccess: (data) => {
      setTableData([data.wallet_summary])
      if (data.invoice_details !== null) 
      setInvoiceTableData(data.invoice_details)
      else setInvoiceTableData([])
    }
  })

  // Payment submission mutation
  const { mutate: submitPayment, isLoading: isSubmittingPayment } = useMutation(
    {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      mutationFn: async (payload: any) => {
        return PaymentCollectionApi.addPaymentReceipt(token, payload)
      },
      onSuccess: () => {
        toast.success('Payment collection added successfully')
        navigate('/hq/b2b/payment-collection')
      },
      onError: (error) => {
        console.error('Payment submission error:', error)
        toast.error('Failed to add payment collection')
      }
    }
  )

  // Memoized company options
  const companyOptions = useMemo((): CompanyOption[] => {
    if (!tenantData || !Array.isArray(tenantData)) return []

    return tenantData.map((company: Company) => ({
      label: company.company_name || '',
      value: company.company_name || '',
      companyId: company.id || '',
      currency: company.currency || '',
      paymentMode: company.commercial_type || ''
    }))
  }, [tenantData])

  const handleCompanyInputChange = useCallback(
    (event: React.SyntheticEvent, value: string) => {
      setCompanySearchTerm(value)
    },
    []
  )

  // Auto-fill fields when company is selected
  useEffect(() => {
    if (companyName?.companyId) {
      setValue('companyId', companyName.companyId)
      // setValue('paymentMode', companyName.paymentMode)
    } else {
      setValue('companyId', '')
    }
  }, [companyName, setValue])

  const onSubmit = (data: FormData) => {
    const payload: Record<string, string> = {
      company_id: data.companyName?.companyId || ''
    }
    if (data.orderNo) payload.order_id = data.orderNo
    if (data.orderDateTo) payload.order_end_date = data.orderDateTo
    if (data.orderDateFrom) payload.order_start_date = data.orderDateFrom
    unpaidPaymentAllocation(payload)
  }

  // ----------Payment form submission-------
  const handlePaymentCollectionSubmit = () => {
    const { referenceNumber, remarks } = getValues()
    const { promoteEsim } = getFormValues()
    const payload: PaymentCollectionReceiptBackendData = {
      tenant_id: companyName?.companyId || '',
      mode_of_payment: 'WALLET',
      reference_no: referenceNumber,
      allocation_remark: remarks,
      created_by: userEmail,
      product: promoteEsim?.value,
      payment_allocation_detail: selectedRowData.map((row) => ({
        order_id: row.order_id,
        invoice_no: row.invoice_id,
        allocation_currency: row.currency,
        received_amount: Number(row.allocatedAmount),
        already_received: row.paid_amount,
        booking_amount: row.order_amount,
        last_pending_amount: row.pending_amount
      }))
    }
    submitPayment(payload)
  }

  const handleReset = () => {
    reset()
    resetPayment()
    setCompanySearchTerm('')
    setTableData([])
    setInvoiceTableData([])
    setSelectedRowIds([])
    setDiscountValues({})
  }
  useEffect(() => {
    setPaymentValue('totalAmountToReceive', totalAllocated, {
      shouldValidate: true,
      shouldDirty: true
    })
    // trigger('totalAmountToReceive'); // if you want immediate validation feedback
  }, [totalAllocated, setValue])

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant='h6' gutterBottom>
        Add / Edit Payment Receipt
      </Typography>

      <Box
        component='form'
        onSubmit={handleSubmit(onSubmit)}
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          gap: 2
        }}
      >
        {/* Company Name (Dynamic Search) */}
        <Controller
          name='companyName'
          control={control}
          rules={{ required: 'Company name is required' }}
          render={({ field, fieldState: { error } }) => (
            <Autocomplete
              {...field}
              options={companyOptions}
              loading={isTenantLoading}
              getOptionLabel={(option: CompanyOption | null) =>
                option?.label || ''
              }
              isOptionEqualToValue={(
                option: CompanyOption,
                value: CompanyOption | null
              ) => option?.value === value?.value}
              onChange={(_, data: CompanyOption | null) => field.onChange(data)}
              onInputChange={handleCompanyInputChange}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label='Company Name'
                  required
                  error={!!error}
                  helperText={
                    error?.message ||
                    (debouncedSearchTerm.length > 0 &&
                    debouncedSearchTerm.length < 2
                      ? 'Type at least 2 characters to search'
                      : '')
                  }
                  placeholder='Type company name to search...'
                  InputProps={{
                    ...params.InputProps
                  }}
                />
              )}
              noOptionsText={
                debouncedSearchTerm.length < 2
                  ? 'Type at least 2 characters to search'
                  : isTenantLoading
                  ? 'Searching...'
                  : tenantFetchError
                  ? 'Error loading companies'
                  : 'No companies found'
              }
            />
          )}
        />

        {/* Company ID (Read Only - Auto-filled) */}
        <Controller
          name='companyId'
          control={control}
          disabled
          render={({ field }) => (
            <TextField
              {...field}
              label='Company ID'
              InputProps={{ readOnly: true }}
              required
              placeholder='Auto-filled when company is selected'
            />
          )}
        />

        {/* Order No */}
        <Controller
          name='orderNo'
          control={control}
          render={({ field }) => (
            <TextField {...field} label='Order Id' placeholder='Order Id ' />
          )}
        />

        {/* E sims */}
        <Controller
          name='promoteEsim'
          control={control}
          render={({ field }) => (
            <Autocomplete
              {...field}
              disablePortal
              disableClearable
              options={ConfirmOptions}
              onChange={(_, data) => field.onChange(data)}
              renderInput={(params) => {
                return <TextField {...params} label='Product' />
              }}
            />
          )}
        />

        {/* Receipt Date */}
        <Controller
          name='orderDateFrom'
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              type='date'
              label='From Date '
              InputLabelProps={{ shrink: true }}
              variant='outlined'
            />
          )}
        />

        <Controller
          name='orderDateTo'
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              type='date'
              label='To Date '
              InputLabelProps={{ shrink: true }}
              variant='outlined'
            />
          )}
        />

        {/* Error display for company search */}
        {tenantFetchError && (
          <Box sx={{ gridColumn: '1 / -1' }}>
            <Typography variant='body2' color='error'>
              Error loading companies
            </Typography>
          </Box>
        )}

        {/* Buttons */}
        <Box sx={{ gridColumn: '1 / -1', display: 'flex', gap: 2 }}>
          <Button variant='outlined' type='button' onClick={handleReset}>
            Reset
          </Button>
          <Button
            variant='contained'
            color='primary'
            type='submit'
            disabled={unpaidPaymentAllocationLoading}
          >
            {unpaidPaymentAllocationLoading ? (
              <CircularProgress size={24} color='inherit' />
            ) : (
              'Search'
            )}
          </Button>
        </Box>
        <Divider sx={{ mb: 2 }} />
      </Box>

      <Box>
        <PaymentDuesList data={tableData} />
      </Box>

      <Box>
        <InvoiceDetailsList
          data={invoiceTableData}
          isLoading={unpaidPaymentAllocationLoading}
          setSelectedRowIds={setSelectedRowIds}
          setDiscountValues={setDiscountValues}
          selectedRowIds={selectedRowIds}
          discountValues={discountValues}
        />
      </Box>

      {/* Payment Details Form */}
      {invoiceTableData.length > 0 && (
        <Paper sx={{ p: 3, mt: 3 }}>
          <Typography variant='h6' gutterBottom>
            Payment Details
          </Typography>
          <Box component='form' sx={{ mt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <Controller
                  name='paymentMode'
                  control={control}
                  defaultValue='WALLET'
                  disabled
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label='Payment Mode'
                      InputProps={{ readOnly: true }}
                      required
                      placeholder='Auto-filled when company is selected'
                    />
                  )}
                />
              </Grid>

              {/* Total Amount to Receive - Mandatory and Disabled */}

              <Grid item xs={12} sm={6} md={3}>
                <Controller
                  name='totalAmountToReceive'
                  control={paymentControl}
                  rules={{
                    required: 'Total amount is required',
                    min: {
                      value: 0.01,
                      message: 'Amount must be greater than 0'
                    },
                    validate: (value) =>
                      value <= walletBalance ||
                      `Amount cannot be greater than wallet balance (${walletBalance})`
                  }}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      value={
                        Number.isFinite(totalAllocated) ? totalAllocated : 0
                      }
                      type='number'
                      label='Total Amount to Receive'
                      required
                      disabled
                      error={!!error || totalAllocated > walletBalance}
                      helperText={
                        error?.message ||
                        (totalAllocated > 0 && totalAllocated > walletBalance
                          ? `Cannot exceed wallet balance: ${walletBalance}`
                          : '')
                      }
                      fullWidth
                      inputProps={{ min: 0, step: 0.01 }}
                    />
                  )}
                />
              </Grid>
              {/* Reference Number */}
              <Grid item xs={12} sm={6} md={3}>
                <Controller
                  name='referenceNumber'
                  control={paymentControl}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label='Reference Number'
                      placeholder='Enter reference number'
                      fullWidth
                    />
                  )}
                />
              </Grid>

              {/* Remarks */}
              <Grid item xs={12} sm={6} md={3}>
                <Controller
                  name='remarks'
                  control={paymentControl}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label='Remarks'
                      placeholder='Enter remarks'
                      multiline
                      rows={1}
                      fullWidth
                    />
                  )}
                />
              </Grid>
            </Grid>

            {/* Validation Messages */}
            {!selectedRowData && invoiceTableData.length > 0 && (
              <Typography variant='body2' color='warning.main' sx={{ mt: 2 }}>
                Please select an invoice row to proceed with payment
              </Typography>
            )}

            {selectedRowData && totalAllocated === 0 && (
              <Typography variant='body2' color='warning.main' sx={{ mt: 2 }}>
                Please enter an amount to be allocated for the selected invoice
              </Typography>
            )}

            {/* Submit Button */}
            <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
              <Button
                variant='contained'
                color='primary'
                // type='submit'
                onClick={() => handlePaymentCollectionSubmit()}
                disabled={!canSubmitPayment || isSubmittingPayment}
                startIcon={
                  isSubmittingPayment ? (
                    <CircularProgress size={16} color='inherit' />
                  ) : null
                }
              >
                {isSubmittingPayment
                  ? 'Submitting...'
                  : 'Submit Payment Collection'}
              </Button>

              <Button
                variant='outlined'
                onClick={() => {
                  resetPayment()
                  // setSelectedRowIds([])
                  setDiscountValues({})
                  // setPaymentValue('totalAmountToReceive', 0)
                }}
                disabled={isSubmittingPayment}
              >
                Reset Payment Details
              </Button>
            </Box>
          </Box>
        </Paper>
      )}

      {tenantFetchError && (
        <Box sx={{ mt: 2, p: 2, bgcolor: '#ffebee', borderRadius: 1 }}>
          <Typography color='error' variant='body2'>
            Error loading companies: {'Unknown error'}
          </Typography>
        </Box>
      )}
    </Box>
  )
}
